import { FilterStateManager } from "./filterStateManager";
import { FilterProcessData } from "./filterProcessData";
import { throttle } from "../index";
import { JobOverviewPage } from "./jobOverviewPage";
import { forEach } from "@splidejs/splide/src/js/utils";

declare const jobData: any;

export class FilterUI {
  private filterProcessData: FilterProcessData;
  private filterStateManager: FilterStateManager;

  private showMoreText = jobData.showMoreText;
  private showLessText = jobData.showLessText;

  private activeFilterBoxWrappers: NodeListOf<HTMLElement> | null = null;

  constructor(filterProcessData: FilterProcessData, filterStateManager: FilterStateManager) {
    this.filterProcessData = filterProcessData;
    this.filterStateManager = filterStateManager;

    this.activeFilterBoxWrappers = document.querySelectorAll(".dt-active-filter-wrapper");
    if (!this.activeFilterBoxWrappers.length) {
      console.warn("No Filter Container found! Cant render active boxes.");
    }

    this.setEvents();
  }

  private setEvents() {
    this.onMinimizeFilter();
    this.bindDelegatedShowMoreClick();
    this.removeActiveFilterBoxEvent();
  }

  // #region Minimize Filter
  private onMinimizeFilter() {
    const throttledMinimize = throttle(this.handleMinimizeFilter.bind(this), 500);
    document.addEventListener("click", (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains("dt-filter-sidebar__category-title")) {
        throttledMinimize(target);
      }
    });
  }

  private handleMinimizeFilter(target: HTMLElement) {
    const collapsible = target.nextElementSibling as HTMLElement;
    if (!collapsible) return;
    collapsible.closest(".dt-filter-sidebar__category")!.classList.toggle("no-margin");
    target.classList.toggle("closed");
    collapsible.classList.toggle("closed");
  }

  private removeActiveFilterBoxEvent() {
    // Event Delegation
    if (!this.activeFilterBoxWrappers) {
      return;
    }
    if (this.activeFilterBoxWrappers && !this.activeFilterBoxWrappers.length) {
      return;
    }

    this.activeFilterBoxWrappers.forEach((element) => {
      element.addEventListener("click", (event: Event) => {
        const target = event.target as HTMLElement;
        const button = target.closest(".dt-active-filter") as HTMLElement;
        if (button) {
          this.removeActiveFilterBox(button);
          console.log("Remove Filterbox:", button);
          this.triggerFilterChange(button);
        }
      });
    });
  }

  /**
   * Triggers when a active-filter-box is removed
   */
  private triggerFilterChange(dtActiveFilterBox: HTMLElement) {
    const categoryName = dtActiveFilterBox?.dataset.categoryName;
    const upperCategory = dtActiveFilterBox?.dataset.categoryGroup;

    if (upperCategory && categoryName) {
      this.filterStateManager.removeActiveFilter(upperCategory, categoryName);
      this.filterStateManager.performFilter();
    }
  }

  /**
   * Removes the active filter box
   */
  private removeActiveFilterBox(dtActiveFilterBox: HTMLElement) {
    dtActiveFilterBox.remove();
  }

  private renderFilterActiveBoxes() {
    if (!this.activeFilterBoxWrappers) {
      return;
    }
    const activeFilters = this.filterStateManager.getActiveFilters();
    // 1. Clear Wrapper
    this.activeFilterBoxWrappers.forEach((element) => {
      element.innerHTML = "";
    });
    // 2. Render Active Filter Boxes
    for (const [upperCategory, categoryNames] of Object.entries(activeFilters)) {
      for (const categoryName of Array.from(categoryNames)) {
        const activeFilterBox = this.activeFilterBoxHTML(categoryName, upperCategory);
        this.activeFilterBoxWrappers.forEach((wrapper) => {
          wrapper.innerHTML += activeFilterBox;
        });
      }
    }
  }

  // <span class="dt-active-filter__upper-category"> ${upperCategory}: </span>
  private activeFilterBoxHTML(categoryName: string, upperCategory: string): string {
    return /*html*/ `
             <button class="dt-active-filter" data-category-group="${upperCategory}" data-category-name="${categoryName}">  
                       <div class="dt-active-filter__content">
                           <span class="dt-active-filter__name"> ${categoryName} </span>
                       </div>
                       <i class="dt-active-filter__icon">
                           <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#272727" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                               <line x1="18" y1="6" x2="6" y2="18"></line>
                               <line x1="6" y1="6" x2="18" y2="18"></line>
                           </svg>
                       </i>
                   </button>
            `;
  }

  public renderFilters() {
    const filterCounts = this.filterProcessData.getSortedFilterCounts(); // for output = categoryName (count)
    const activeFilters = this.filterStateManager.getActiveFilters(); // to know which filters are active

    // Render Active Filter Boxes
    this.renderFilterActiveBoxes();

    this.renderFilterInAllSpecificContainers(
      ".js-sidebar-working-times-content",
      "workingTimes",
      filterCounts,
      activeFilters
    );

    this.renderFilterInAllSpecificContainers(".js-sidebar-joblevel-content", "jobLevel", filterCounts, activeFilters);

    this.renderFilterInAllSpecificContainers(
      ".js-sidebar-jobopening-content",
      "jobOpening",
      filterCounts,
      activeFilters
    );
  }

  /** Renders the filter in all specific containers (e.g. jobLevel)
   *
   * @param identifier - e.g. ".js-sidebar-joblevel-content"
   * @param upperCategory - e.g. "jobLevel"
   * @returns
   */
  renderFilterInAllSpecificContainers(
    identifier: string,
    upperCategory: string,
    filterCounts: Record<string, Record<string, number>>,
    activeFilters: Record<string, Set<string>>
  ) {
    const upperCategoryContainer = document.querySelectorAll(identifier) as NodeListOf<HTMLElement>;


    if (upperCategoryContainer.length === 0) {
      console.warn("No UpperCategoryContainer found!");
      return;
    }

    upperCategoryContainer.forEach((container) => {
      container.innerHTML = this.createFilterHTML(
        upperCategory,
        filterCounts[upperCategory],
        activeFilters[upperCategory]
      );
    });
  }


  /**
   * 
   */
  private createFilterHTML(
    upperCategory: string,
    filters: Record<string, number>,
    activeFilters?: Set<string>
  ): string {
    let html = "";

    // Open Class
    const isOpenClass = this.filterStateManager.getOpenState(upperCategory) ? "open" : "";

    // 1. Render checkboxes for each filter in the category
    for (const [name, count] of Object.entries(filters)) {
      const checked = activeFilters?.has(name) ? "checked" : "";
      const isActiveClass = activeFilters?.has(name) ? "active" : "";
      html += /*html*/ ` 
            <div class="dt-filter-sidebar__checkbox-row ${isOpenClass} ${isActiveClass}"
             data-category-group="${upperCategory}" data-category-name="${name}"> 
          <input type="checkbox" id="${name}" 
           data-category="${upperCategory}" data-value="${name}" class="dt-filter-sidebar__checkbox-row-input" ${checked}>
          <label for="${name}" class="dt-filter-sidebar__checkbox-row-label" >${name}</label>
          <div class="dt-filter-sidebar__checkbox-row-count">${count}</div>
        </div>
      `;
    }

    // 2. Creates Show More Button if there are more than 5 filters in the category
    if (Object.entries(filters).length > JobOverviewPage.standardShownFilter) {
      html += this.createShowMoreBtnHTML(upperCategory, this.filterStateManager.getOpenState(upperCategory));
    }

    return html;
  }

  private createShowMoreBtnHTML(upperCategory: string, isOpen: boolean): string {
    if (!isOpen) {
      return /*html*/ `
      <button class="dt-filter-sidebar__show-more"type="button" data-category-group="${upperCategory}">
         <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
             <path id="Vector" d="M11.3224 7.27889H7.22005V11.3813C7.22005 11.5989 7.13361 11.8076 6.97974 11.9614C6.82587 12.1153 6.61718 12.2017 6.39958 12.2017C6.18197 12.2017 5.97328 12.1153 5.81941 11.9614C5.66554 11.8076 5.5791 11.5989 5.5791 11.3813V7.27889H1.47673C1.25912 7.27889 1.05043 7.19245 0.896562 7.03858C0.742693 6.88471 0.65625 6.67602 0.65625 6.45841C0.65625 6.24081 0.742693 6.03212 0.896562 5.87825C1.05043 5.72438 1.25912 5.63794 1.47673 5.63794H5.5791V1.53556C5.5791 1.31796 5.66554 1.10927 5.81941 0.955399C5.97328 0.80153 6.18197 0.715088 6.39958 0.715088C6.61718 0.715088 6.82587 0.80153 6.97974 0.955399C7.13361 1.10927 7.22005 1.31796 7.22005 1.53556V5.63794H11.3224C11.54 5.63794 11.7487 5.72438 11.9026 5.87825C12.0565 6.03212 12.1429 6.24081 12.1429 6.45841C12.1429 6.67602 12.0565 6.88471 11.9026 7.03858C11.7487 7.19245 11.54 7.27889 11.3224 7.27889Z" fill="#3F5371" />
         </svg>
         <span> ${this.showMoreText} </span>
       </button>`;
    } else {
      return /*html*/ `
      <button class="dt-filter-sidebar__show-more active" type="button" data-category-group="${upperCategory}">
         <svg width="13" height="13"  fill="#000000" width="800px" height="800px" viewBox="0 0 24 24" id="minus" data-name="Flat Color" xmlns="http://www.w3.org/2000/svg" class="icon flat-color">
            <path id="primary" d="M19,13H5a1,1,0,0,1,0-2H19a1,1,0,0,1,0,2Z" style="fill: #3F5371;"></path>
         </svg>
         <span> ${this.showLessText} </span>
       </button>`;
    }
  }

  // #region Show More Button

  /**
   * Binds the click event to the show more button using event delegation.
   */
  private bindDelegatedShowMoreClick() {
    const filterForms = document.querySelectorAll(".dt-filter-sidebar__filter-form");
    if (!filterForms.length) {
      console.error("Filter form not found!");
      return;
    }

    filterForms.forEach((form) => {
      form.addEventListener("click", (event: Event) => {
        let target = event.target as HTMLElement;
        const button = target.closest(".dt-filter-sidebar__show-more");
        if (button) {
          console.log(".dt-filter-sidebar__show-more clicked!");
          this.handleShowMoreButtonClick(button);
        }
      });
    });
  }

  private handleShowMoreButtonClick(button: Element) {
    const categoryContainer = button.closest(".dt-filter-sidebar__category") as HTMLElement;
    if (!categoryContainer) return;

    const upperCategory = categoryContainer.dataset.categoryGroup as string;

    const isOpen = this.filterStateManager.getOpenState(upperCategory);
    if (isOpen) {
      this.closeAllFilters(button, categoryContainer);
    } else {
      this.showAllFilters(button, categoryContainer);
    }
  }

  /**
   * Closes all filters of the uppercategory (e.g. workingTimes, jobLevel, jobOpening)
   */
  private closeAllFilters(element: Element, categoryContainer: HTMLElement) {
    this.toggleCheckboxVisibility(categoryContainer, false);

    const plusIcon = `<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg"> <path id="Vector" d="M11.3224 7.27889H7.22005V11.3813C7.22005 11.5989 7.13361 11.8076 6.97974 11.9614C6.82587 12.1153 6.61718 12.2017 6.39958 12.2017C6.18197 12.2017 5.97328 12.1153 5.81941 11.9614C5.66554 11.8076 5.5791 11.5989 5.5791 11.3813V7.27889H1.47673C1.25912 7.27889 1.05043 7.19245 0.896562 7.03858C0.742693 6.88471 0.65625 6.67602 0.65625 6.45841C0.65625 6.24081 0.742693 6.03212 0.896562 5.87825C1.05043 5.72438 1.25912 5.63794 1.47673 5.63794H5.5791V1.53556C5.5791 1.31796 5.66554 1.10927 5.81941 0.955399C5.97328 0.80153 6.18197 0.715088 6.39958 0.715088C6.61718 0.715088 6.82587 0.80153 6.97974 0.955399C7.13361 1.10927 7.22005 1.31796 7.22005 1.53556V5.63794H11.3224C11.54 5.63794 11.7487 5.72438 11.9026 5.87825C12.0565 6.03212 12.1429 6.24081 12.1429 6.45841C12.1429 6.67602 12.0565 6.88471 11.9026 7.03858C11.7487 7.19245 11.54 7.27889 11.3224 7.27889Z" fill="#3F5371"></path> </svg>`;

    element.innerHTML = plusIcon + `<span> ${this.showMoreText} </span>`;
    element.classList.remove("active");

    // Handles open State of the filters
    const categoryGroup = categoryContainer.dataset.categoryGroup as string;
    this.filterStateManager.setOpenState(categoryGroup, false);
  }

  /**
   * Shows all Filters of the uppercategory (e.g. workingTimes, jobLevel, jobOpening)
   */
  private showAllFilters(element: Element, categoryContainer: HTMLElement) {
    // 2. Checkboxes toggle open class
    this.toggleCheckboxVisibility(categoryContainer, true);

    const minusIcon = `<svg width="13" height="13"  fill="#000000" width="800px" height="800px" viewBox="0 0 24 24" id="minus" data-name="Flat Color" xmlns="http://www.w3.org/2000/svg" class="icon flat-color"><path id="primary" d="M19,13H5a1,1,0,0,1,0-2H19a1,1,0,0,1,0,2Z" style="fill: #3F5371;"></path></svg>`;

    element.innerHTML = minusIcon + `<span> ${this.showLessText} </span>`;
    element.classList.add("active");

    // Handles open State of the filters
    const categoryGroup = categoryContainer.dataset.categoryGroup as string;
    this.filterStateManager.setOpenState(categoryGroup, true);
  }

  private toggleCheckboxVisibility(categoryContainer: HTMLElement, open: boolean) {
    const checkboxes = categoryContainer.querySelectorAll(".dt-filter-sidebar__checkbox-row");
    checkboxes.forEach((checkbox) => {
      checkbox.classList.toggle("open", open);
    });
  }
  // #endregion
}
